import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDashboardLayouts, type DashboardLayoutResponse } from '../hooks/useDashboardLayout';
import { cn } from '../../../lib/utils';

const ChevronDown = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const CheckIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const GearIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

export interface DashboardSwitcherProps {
  /** Current layout name from URL (undefined = default layout) */
  currentLayoutName: string | undefined;
  onSaveAsClick: () => void;
  /** Open dashboard settings (widgets, groups, rename/delete dashboards) */
  onManageClick?: () => void;
}

const DashboardSwitcher: React.FC<DashboardSwitcherProps> = ({ currentLayoutName, onSaveAsClick, onManageClick }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { layouts, isLoading } = useDashboardLayouts();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const defaultLayout = layouts.find((l) => l.isDefault);
  const currentDisplayName = currentLayoutName
    ? layouts.find((l) => l.name === currentLayoutName)?.name ?? currentLayoutName
    : defaultLayout?.name ?? 'Dashboard';

  const setDashboard = (name: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (name) next.set('dashboard', name);
    else next.delete('dashboard');
    navigate({ pathname: '/dashboard', search: next.toString() });
    setOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [open]);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-2 rounded-xl border px-3 py-2.5 min-h-[44px] sm:min-h-0 text-sm font-medium transition-colors',
          isDark
            ? 'border-gray-600 bg-gray-800/60 text-gray-200 hover:bg-gray-700/60'
            : 'border-gray-200 bg-white/80 text-gray-800 shadow-sm hover:bg-gray-50'
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Switch dashboard"
      >
        <span className="truncate max-w-[140px] sm:max-w-[200px]">{currentDisplayName}</span>
        <ChevronDown className="w-4 h-4 shrink-0" />
      </button>

      {open && (
        <div
          className={cn(
            'absolute left-0 top-full z-50 mt-1 min-w-[200px] rounded-xl border shadow-lg py-1 max-h-[min(60vh,320px)] overflow-y-auto',
            isDark ? 'border-gray-600 bg-gray-800' : 'border-gray-200 bg-white'
          )}
          role="listbox"
        >
          {isLoading ? (
            <div className={cn('px-3 py-4 text-sm', isDark ? 'text-gray-400' : 'text-gray-500')}>
              Loading…
            </div>
          ) : (
            <>
              {layouts.map((layout: DashboardLayoutResponse) => {
                const isSelected =
                  (currentLayoutName && layout.name === currentLayoutName) ||
                  (!currentLayoutName && layout.isDefault);
                return (
                  <button
                    key={layout.id}
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => setDashboard(layout.isDefault ? null : layout.name)}
                    className={cn(
                      'w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                      isSelected
                        ? isDark
                          ? 'bg-teal-500/20 text-teal-400'
                          : 'bg-teal-50 text-teal-700'
                        : isDark
                          ? 'text-gray-200 hover:bg-gray-700/60'
                          : 'text-gray-800 hover:bg-gray-50'
                    )}
                  >
                    <span className="truncate">{layout.name}</span>
                    {layout.isDefault && (
                      <span className={cn('text-xs shrink-0', isDark ? 'text-gray-400' : 'text-gray-500')}>
                        Default
                      </span>
                    )}
                    {isSelected && <CheckIcon className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
              <div className={cn('border-t my-1', isDark ? 'border-gray-600' : 'border-gray-200')} />
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSaveAsClick();
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm font-medium transition-colors',
                  isDark ? 'text-teal-400 hover:bg-gray-700/60' : 'text-teal-600 hover:bg-teal-50'
                )}
              >
                <PlusIcon className="w-4 h-4" />
                Save as new dashboard
              </button>
              {onManageClick && (
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onManageClick();
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors',
                    isDark ? 'text-gray-400 hover:bg-gray-700/60' : 'text-gray-600 hover:bg-gray-50'
                  )}
                >
                  <GearIcon className="w-4 h-4" />
                  Manage dashboards (rename, delete)
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardSwitcher;
